import pick from 'lodash/pick';

import config from '../../config';
import { types as sdkTypes, util as sdkUtil, createImageVariantConfig } from '../../util/sdkLoader';
import { getStartOf, addTime } from '../../util/dates';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  cancelCartReminderEmailToUser,
  cartItemReminderEmailToUser,
  fetchUserStripeAccount,
  reportingUserEmail,
  transactionLineItems,
  updateAuthorListings,
  updateTransactionMetadata,
} from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { TRANSITION_ENQUIRE, TRANSITION_MAKE_AN_OFFER_BY_CUSTOMER } from '../../util/transaction';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  parse,
} from '../../util/urlHelpers';
import { fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { FOR_SALE } from '../../util/types';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isStockInUse } from '../../util/search';
import { LISTING_AVAILABLE, listingAvailability } from '../../util/listingAvailability';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ListingPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ListingPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ListingPage/SEND_ENQUIRY_ERROR';

export const REPORT_USER_REQUEST = 'app/ListingPage/REPORT_USER_REQUEST';
export const REPORT_USER_SUCCESS = 'app/ListingPage/REPORT_USER_SUCCESS';
export const REPORT_USER_ERROR = 'app/ListingPage/REPORT_USER_ERROR';

export const SEARCH_LISTINGS_REQUEST = 'app/ListingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/ListingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/ListingPage/SEARCH_LISTINGS_ERROR';

export const FETCH_USER_STRIPE_SUCCESS = 'app/ListingPage/FETCH_USER_STRIPE_SUCCESS';

export const FETCH_USER_ENQUIRY_SUCCESS = 'app/ListingPage/FETCH_USER_ENQUIRY_SUCCESS';

export const FETCH_SIMILAR_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_SUCCESS';

// ================ Reducer ================ //
const resultIds = data => data.data.map(l => l.id);
const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendEnquiryInProgress: false,
  sendEnquiryError: null,
  enquiryModalOpenForListingId: null,
  reportUserInProgress: false,
  reportUserError: null,
  currentPageResultIds: [],
  searchInProgress: false,
  pagination: null,
  searchListingsError: null,
  isStripeConnected: false,
  authorData: null,
  // Similar items shown on a sold or closed listing (BSC-15)
  similarListingIds: [],

  enquireId: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchInProgress: true,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case FETCH_USER_STRIPE_SUCCESS:
      return {
        ...state,
        isStripeConnected: payload.response,
        authorData: payload.userData,
      };
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null, similarListingIds: [] };
    case FETCH_SIMILAR_LISTINGS_SUCCESS:
      return { ...state, similarListingIds: payload };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case REPORT_USER_REQUEST:
      return { ...state, reportUserInProgress: true, reportUserError: null };
    case REPORT_USER_SUCCESS:
      return { ...state, reportUserInProgress: false, reportUserError: null };

    case FETCH_TIME_SLOTS_REQUEST:
      return { ...state, fetchTimeSlotsError: null };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, timeSlots: payload };
    case FETCH_TIME_SLOTS_ERROR:
      return { ...state, fetchTimeSlotsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryInProgress: false };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };

    case FETCH_USER_ENQUIRY_SUCCESS:
      return {
        ...state,
        enquireId: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const searchListingsRequest = () => ({
  type: SEARCH_LISTINGS_REQUEST,
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const fetchUserStripeSuccess = (response, userData) => ({
  type: FETCH_USER_STRIPE_SUCCESS,
  payload: { response, userData },
});

export const reportUserRequest = () => ({ type: REPORT_USER_REQUEST });
export const reportUserSuccess = () => ({ type: REPORT_USER_SUCCESS });

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = () => ({ type: FETCH_TIME_SLOTS_REQUEST });
export const fetchTimeSlotsSuccess = timeSlots => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: timeSlots,
});
export const fetchTimeSlotsError = error => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = () => ({ type: SEND_ENQUIRY_SUCCESS });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });

export const fetchEnquirySuccess = response => ({
  type: FETCH_USER_ENQUIRY_SUCCESS,
  payload: response,
});
// ================ Thunks ================ //

export const fetchStripeDetails = userId => (dispatch, getState, sdk) => {
  return fetchUserStripeAccount({ userId })
    .then(response => {
      if (response?.data?.data) {
        const userData = response.data.data;
        const stripeConnected = userData.attributes?.stripeConnected ?? false;
        dispatch(fetchUserStripeSuccess(stripeConnected, userData));
      }
      return response;
    })
    .catch(stripeError => {
      // A failed/missing Stripe account lookup just means the seller hasn't
      // connected Stripe yet (isStripeConnected stays false by default) -
      // it must not be treated as a page-level listing error.
      console.error('Stripe account fetch error:', stripeError);
    });
};

export const showListing = (queryParams, listingId, isOwn = false) => (dispatch, getState, sdk) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock', 'author.stripeAccount'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.facebook',
      'variants.twitter',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      dispatch(addMarketplaceEntities(data));
      const userId = data.data.data.relationships?.author?.data?.id?.uuid;
      dispatch(fetchOtherListings(userId, queryParams));
      // A sold or closed listing suggests similar items a buyer can still get. Never let this
      // affect loading the listing itself.
      try {
        const [listing] = denormalisedResponseEntities(data);
        if (listing && listingAvailability(listing) !== LISTING_AVAILABLE) {
          dispatch(fetchSimilarListings(listing));
        }
      } catch (error) {
        log.error(error, 'similar-listings-skipped');
      }
      return data;
    })
    .catch(error => {
      dispatch(showListingError(storableError(error)));
    });
};

export const fetchReviews = userId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const sendReportUserEmail = data => async (dispatch, getState, sdk) => {
  try {
    dispatch(reportUserRequest());
    const result = await reportingUserEmail({ data });
    if (result) {
      dispatch(reportUserSuccess());
    }
    return result;
  } catch (error) {}
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchTimeSlotsRequest);

  // Time slots can be fetched for 90 days at a time,
  // for at most 180 days from now. If max number of bookable
  // day exceeds 90, a second request is made.

  const maxTimeSlots = 90;
  // booking range: today + bookable days -1
  const bookingRange = config.dayCountAvailableForBooking - 1;
  const timeSlotsRange = Math.min(bookingRange, maxTimeSlots);

  const now = new Date();
  const start = getStartOf(now, 'day', 'Etc/UTC');
  const end = addTime(start, timeSlotsRange, 'days', 'Etc/UTC');
  const params = { listingId, start, end };

  return dispatch(timeSlotsRequest(params))
    .then(timeSlots => {
      const secondRequest = bookingRange > maxTimeSlots;

      if (secondRequest) {
        const secondRange = Math.min(maxTimeSlots, bookingRange - maxTimeSlots);
        const secondParams = {
          listingId,
          start: end,
          end: addTime(end, secondRange, 'days', 'Etc/UTC'),
        };

        return dispatch(timeSlotsRequest(secondParams)).then(secondBatch => {
          const combined = timeSlots.concat(secondBatch);
          dispatch(fetchTimeSlotsSuccess(combined));
        });
      } else {
        dispatch(fetchTimeSlotsSuccess(timeSlots));
      }
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(storableError(e)));
    });
};

export const sendEnquiry = (listingId, message) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());
  const bodyParams = {
    transition: TRANSITION_ENQUIRE,
    processAlias: config.transactionProcessAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendEnquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        updateTransactionMetadata({
          id: transactionId,
          isReadByProvider: false,
        });
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const sendOffer = (listingId, message, data) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());
  const { listingPrice, offerPrice, listingTitle } = data || {};
  const bodyParams = {
    transition: TRANSITION_MAKE_AN_OFFER_BY_CUSTOMER,
    processAlias: config.transactionProcessAlias,
    params: {
      listingId,
      protectedData: {
        listingPrice,
        offerPrice,
        isOfferTransaction: true,
        listingTitle,
      },
    },
  };

  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;
      // const { ...rest } = response.data.data.attributes?.metadata;
      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendEnquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        updateTransactionMetadata({
          id: transactionId,
          isProviderRead: false,
          isCustomerRead: true,
          isReadByProvider: false,
        });
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const updateListingData = listingdata => async dispatch => {
  try {
    const result = await updateAuthorListings(listingdata);
    if (result) {
      dispatch(showListing(listingdata.listingId));
    }
  } catch (error) {}
};

export const fetchOtherListings = (userId, queryParams) => async (dispatch, getState, sdk) => {
  try {
    dispatch(searchListingsRequest());
    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
    const { page = 1 } = queryParams;
    const aspectRatio = aspectHeight / aspectWidth;
    const minStockMaybe = isStockInUse(config) ? { minStock: 1 } : {};

    const result = await sdk.listings.query({
      // ...minStockMaybe,
      authorId: userId,
      pub_isDefaultForMessage: false,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      'fields.image': [
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,

        // Social media
        'variants.facebook',
        'variants.twitter',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),

      'limit.images': 5,
      per_page: 40,
      page,
    });
    dispatch(addMarketplaceEntities(result));
    dispatch(searchListingsSuccess(result));
    return result;
  } catch (error) {}
};

export const SIMILAR_LISTINGS_COUNT = 8;

/**
 * Searches, in order, for similar listings to a sold or closed one: same brand, then same
 * category. Each is an extra filter on a search for published listings with stock left.
 */
export const similarListingFilters = listing => {
  const { brand, category } = listing?.attributes?.publicData || {};
  return [brand && { pub_brand: brand }, category && { pub_category: category }].filter(Boolean);
};

// Similar items for a sold or closed listing (BSC-15): up to SIMILAR_LISTINGS_COUNT listings by
// brand, or by category when no other listing has the brand. Never the listing itself, never a
// hidden default-message listing, only listings that can be bought. A failure leaves the section
// out; it never affects the page.
export const fetchSimilarListings = listing => async (dispatch, getState, sdk) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;
  const listingUuid = listing?.id?.uuid;

  try {
    for (const filter of similarListingFilters(listing)) {
      const response = await sdk.listings.query({
        ...filter,
        minStock: 1,
        pub_isDefaultForMessage: false,
        include: ['author', 'images', 'currentStock'],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          `variants.${variantPrefix}-4x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
        'limit.images': 1,
        perPage: SIMILAR_LISTINGS_COUNT + 1,
      });
      const ids = response.data.data
        .map(l => l.id)
        .filter(id => id.uuid !== listingUuid)
        .slice(0, SIMILAR_LISTINGS_COUNT);
      if (ids.length > 0) {
        dispatch(addMarketplaceEntities(response));
        dispatch({ type: FETCH_SIMILAR_LISTINGS_SUCCESS, payload: ids });
        return ids;
      }
    }
  } catch (error) {
    log.error(error, 'fetch-similar-listings-failed', { listingId: listingUuid });
  }
  return [];
};

export const cartItemNotification = emailData => async (dispatch, getState, sdk) => {
  try {
    const result = await cartItemReminderEmailToUser(emailData);
    return result;
  } catch (error) {}
};

export const cancelCartItemNotification = batch_id => async () => {
  try {
    const result = await cancelCartReminderEmailToUser(batch_id);
    return result;
  } catch (error) {}
};

export const usersInquiryTransaction = listingId => (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  if (currentUser?.id?.uuid) {
    return sdk.transactions
      .query({
        only: 'order',
        lastTransitions: TRANSITION_ENQUIRE,
        listingId: listingId?.uuid,
      })
      .then(res => {
        if (res) {
          const txId = res?.data?.data[0]?.id;
          dispatch(fetchEnquirySuccess(txId));
          return res;
        }
      });
  } else {
    return null;
  }
};

export const loadData = (params, search) => dispatch => {
  const listingId = new UUID(params.id);
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(queryParams, listingId, true));
  }

  if (config.listingManagementType === 'availability') {
    return Promise.all([
      dispatch(showListing(queryParams, listingId)).then(res => {}),
      dispatch(fetchTimeSlots(listingId)),
      dispatch(usersInquiryTransaction(listingId)),
    ]);
  } else {
    return Promise.all([
      dispatch(showListing(queryParams, listingId)).then(res => {
        const userId = res?.data?.data?.relationships?.author?.data?.id;
        console.log(userId, 'userIduserId');

        dispatch(fetchReviews(userId));
        dispatch(usersInquiryTransaction(listingId));
        // dispatch(fetchStripeDetails(userId.uuid));
      }),
    ]);
  }
};
